import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, BookOpen, Sparkles } from "lucide-react";
import api from "@/lib/api";
import MemberLayout from "@/components/layout/MemberLayout";

export default function MemberDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Meu Espaço — Ana Paula Moraes";
    api.get("/member/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <MemberLayout>
        <div className="p-12 flex items-center gap-3" style={{ color: "var(--text-secondary)" }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: "var(--rose-gold)", borderTopColor: "transparent" }} />
          <span>Carregando seu espaço…</span>
        </div>
      </MemberLayout>
    );
  }

  const { courses, last_lesson, user } = data;

  return (
    <MemberLayout>
      <div className="p-8 lg:p-16 max-w-7xl mx-auto">
        {/* Welcome header */}
        <div className="mb-14 fade-in-up">
          <div className="flex items-center gap-3 mb-5">
            <span className="dot-ornament" />
            <span className="eyebrow">Olá, {user.name.split(" ")[0]}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-balance" style={{ color: "var(--text-primary)" }}>
            Bem-vinda ao{" "}
            <span className="headline-italic-accent">seu espaço</span>.
          </h1>
          <p className="mt-5 body-lead max-w-xl">
            Continue sua jornada onde parou. Cada pequeno passo importa.
          </p>
        </div>

        {/* Continue watching */}
        {last_lesson && (
          <div
            className="brand-card-flat p-8 md:p-10 mb-14 grid md:grid-cols-3 gap-6 items-center relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(196,143,149,0.10), rgba(184,115,51,0.04))", border: "1px solid rgba(196,143,149,0.2)" }}
            data-testid="continue-watching"
          >
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full" style={{ background: "rgba(196,143,149,0.12)" }} />
            <div className="md:col-span-2 relative">
              <div className="flex items-center gap-2 mb-3">
                <Play size={12} fill="currentColor" style={{ color: "var(--copper)" }} />
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--copper)" }}>
                  Continue assistindo
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mb-2 leading-tight" style={{ color: "var(--text-primary)" }}>
                {last_lesson.lesson.title}
              </h3>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{last_lesson.course?.title}</p>
            </div>
            <Link to={`/membros/aula/${last_lesson.lesson.id}`} className="btn-primary md:justify-self-end relative" data-testid="continue-watching-btn">
              <Play size={14} fill="currentColor" /> Continuar
            </Link>
          </div>
        )}

        {/* Section header */}
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="divider-ornament eyebrow mb-3">Sua biblioteca</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight" style={{ color: "var(--text-primary)" }}>Meus conteúdos</h2>
          </div>
          <span className="text-sm hidden md:block" style={{ color: "var(--text-muted)" }}>
            {courses.length} {courses.length === 1 ? "curso" : "cursos"}
          </span>
        </div>

        {/* Course grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {courses.length === 0 && (
            <div className="col-span-full brand-card text-center py-20 px-8">
              <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "rgba(196,143,149,0.12)" }}>
                <Sparkles size={24} style={{ color: "var(--rose-gold)" }} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>Em breve</h3>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                Você ainda não tem cursos liberados. Em breve novos conteúdos chegarão por aqui.
              </p>
            </div>
          )}
          {courses.map((c, i) => (
            <Link
              to={`/membros/curso/${c.id}`}
              key={c.id}
              className="brand-card overflow-hidden block fade-in-up group"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`member-course-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={c.cover_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {c.progress_pct === 100 && (
                  <div className="absolute top-4 right-4 pill-soft bg-white">
                    Concluído
                  </div>
                )}
              </div>
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: "var(--copper)" }}>
                  <span className="serial-mark">N° {String(i + 1).padStart(2, '0')}</span>
                  <span className="dot-ornament opacity-40" />
                  <span>Curso</span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl mb-4 leading-tight" style={{ color: "var(--text-primary)" }}>{c.title}</h3>

                <div className="mb-5">
                  <div className="flex justify-between text-xs mb-2" style={{ color: "var(--text-secondary)" }}>
                    <span>{c.completed_count}/{c.total_lessons} aulas</span>
                    <span className="font-semibold" style={{ color: "var(--copper)" }}>{c.progress_pct}%</span>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--border-light)" }}>
                    <div
                      className="h-full transition-all duration-1000"
                      style={{
                        width: `${c.progress_pct}%`,
                        background: "linear-gradient(90deg, var(--rose-gold), var(--copper))",
                      }}
                    />
                  </div>
                </div>
                <span className="btn-ghost-link">
                  Continuar <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </MemberLayout>
  );
}
